/**
 * The recipient's page (`KLQ-20260826-002`).
 *
 * **Read by somebody who has never seen Kalqa**, arrived from an email, and will be here
 * once. So the copy names facts rather than features: what this is, who published it, when it
 * was issued, and — when there is nothing to show — what to do next.
 *
 * **The dead-end pair is a product decision, not filler** (§E). `gone.title` and `gone.body`
 * are the one screen an expired share, a revoked share and a token that never existed all
 * reach, because the API answers all three identically (`NO_SUCH_SHARE`). They may therefore
 * never say "expired", "revoked" or "no longer valid": each of those asserts that a token was
 * once alive, which is the disclosure `KLQ-20260814-005` §D forbids at the API and which a
 * page can leak just as easily. What they say instead is that this address shows nothing, and
 * that a new link is asked for from the person who sent this one.
 *
 * There is no call to action here beyond that, and its absence is deliberate: a share is
 * somebody's professional document sent under a disclosure record, not a funnel. The product
 * names itself once, small, at the bottom.
 */
export const shareEn = {
  wordmark: 'KALQA',
  loading: 'Loading…',
  failed: 'This page could not be loaded. Try again.',

  // The one dead end. See the note above before changing a word of these two.
  'gone.title': 'Nothing to show at this address',
  'gone.body': 'Ask the person who sent you this link to send a new one.',

  'kind.version': 'Drawing version',
  'kind.review': 'Review',
  'kind.task': 'Task',
  'kind.transmittal': 'Transmittal',

  'header.slot': 'Drawing {slot}',
  'header.version': 'Version {label}',
  // The firm, never the person who pressed publish (§C, ratified 2026-08-14). The line is
  // omitted entirely when the payload names no studio — there is no "unknown" to render.
  'header.studio': 'Published by {studio}',
  'header.issued': 'Issued {date}',
  // `MCK-20260812-011`'s seam. Rendered only when the payload names one.
  'header.phase': 'Phase {phase}',
  'header.expires': 'This link works until {date}.',

  // §E. A sentence rather than the spec's `r03 — superseded by r04`: the catalog gate refuses a
  // spaced dash joining two clauses, and what the criterion asks for is the successor named by
  // its label. There is no link and nothing to press — the payload carries no id to route to.
  'superseded': 'Version {label} has been superseded by version {successor}.',

  // A comparison share renders both sheets at once rather than offering a switch (§C), so each
  // one says which it is.
  'compare.shared': 'Shared version {label}',
  'compare.base': 'Compared with version {label}',

  // The guest viewer (§D). Pan, zoom, fit, page navigation, print. There is no key here for a
  // pin, a markup tool, a comment or a version switcher, because there is no control for one.
  'viewer.zoomIn': 'Zoom in',
  'viewer.zoomOut': 'Zoom out',
  'viewer.fit': 'Fit to window',
  'viewer.previousPage': 'Previous page',
  'viewer.nextPage': 'Next page',
  'viewer.pageOf': 'Page {page} of {count}',
  'viewer.print': 'Print',
  'viewer.notViewable': 'This sheet cannot be viewed through this link.',
  'viewer.preparing': 'This drawing is still being prepared. Reload the page in a moment.',
  'viewer.unavailable': 'This drawing cannot be displayed.',

  // Rendered only where the sender permitted a download; there is no disabled version of this
  // control, because there is no door behind it either. The five minutes are stated before the
  // press and in those words: a download that fails at minute six reads as a broken product.
  'download.action': 'Download PDF',
  'download.validity': 'The download link works for five minutes.',
  'download.failed': 'The download could not be started. Try again.',

  // §C, the review row. A decision and its date, and nothing that would suggest a conversation
  // sits behind it: the payload declares no thread, no reviewer and no note, so there is no
  // key here for an empty one either. Sentences rather than enum values, and never a coloured
  // chip whose meaning is only the colour — the reader has no legend and will not learn one.
  'review.decision.approve': 'Approved.',
  'review.decision.approveWithComments': 'Approved with comments.',
  'review.decision.requestChanges': 'Changes requested.',
  'review.state.open': 'This review has not been decided yet.',
  'review.state.decided': 'This review has been decided.',
  'review.state.cancelled': 'This review was withdrawn.',
  'review.decidedOn': 'Decided {date}',
  // The firm that asked for the decision, never the person who owed it. Omitted where the
  // payload names none, exactly as the publishing studio is.
  'review.requestedBy': 'Requested by {studio}',

  // §C, the task row: title, status, due date. The title is the payload's own words and needs
  // no key; the state is a sentence for the same reason a decision is. No key for a discussion,
  // an assignee or a note, because there is no field for one.
  'task.state.open': 'This task is open.',
  'task.state.inProgress': 'This task is in progress.',
  'task.state.done': 'This task is done.',
  'task.state.closed': 'This task is closed.',
  'task.due': 'Due {date}',

  // A transmittal, as its recipient reads it (`MCK-20260812-010`). The vocabulary is sent,
  // delivered, opened, downloaded, and this page only ever needs the first: it is what was sent.
  // The role is the one the issuer held at issue, as a word rather than the enum.
  'transmittal.project': 'Project {project}',
  'transmittal.issuedBy':
    'Sent by {name} ({role, select, admin {administrator} manager {manager} architect {architect} other {project member}})',
  // The issuer's name is gone once they are erased; the role they sent it as is not.
  'transmittal.issuedAs':
    'Sent by the {role, select, admin {administrator} manager {manager} architect {architect} other {project member}}',
  'transmittal.issuedOn': 'Sent {date}',
  'transmittal.reason': 'Reason for issue: {reason}',
  'transmittal.message': 'Message',
  'transmittal.items': 'Drawings sent',
  'transmittal.column.drawing': 'Drawing',
  'transmittal.column.title': 'Title',
  'transmittal.column.version': 'Version',
  'transmittal.column.file': 'File',
  'transmittal.column.action': 'Action',
  // A later transmittal to this same reader carried this drawing again. A label, nothing to
  // press, and never a mention of a version they were not sent.
  'transmittal.superseded': 'Superseded by {label}',
  'transmittal.noFile': 'No file',
  'transmittal.sha256': 'SHA-256 {hash}',
  'transmittal.open': 'Open drawing',
  // On an item's drawing: the way back to the transmittal it was opened from.
  'transmittal.back': 'Back to the transmittal',

  // §B row 3: a visitor signed in as somebody else is rendered under the share session and
  // never switched. The page says which one it is showing, without naming the recipient —
  // that half is deferred to the owner (the plan's Deferred section).
  'session.notice':
    'You are reading this through the link that was sent to you. No account is signed in on this page.',
} as const;

export type ShareMessageKey = keyof typeof shareEn;
